import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createEffect, createSignal } from "solid-js";
import {
  createProject,
  deleteProject,
  fetchMyProjects,
  fetchSkills,
  updateProject,
} from "~/lib/api";
import { SKILL_CATEGORY_LABELS, skillsByCategory } from "~/lib/skills";

const MAX_TITLE = 100;
const MAX_SUMMARY = 160;
const MAX_DESCRIPTION = 4000;
const MAX_SKILLS = 15;

type FormValues = {
  title: string;
  summary: string;
  description: string;
  repoUrl: string;
  demoUrl: string;
  articleUrl: string;
  isPinned: boolean;
  visibility: "hidden" | "directory";
  skillIds: string[];
};

const emptyForm = (): FormValues => ({
  title: "",
  summary: "",
  description: "",
  repoUrl: "",
  demoUrl: "",
  articleUrl: "",
  isPinned: false,
  visibility: "directory",
  skillIds: [],
});

export function EditProjectPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = () => params().projectId ?? "";
  const isNew = () => !projectId();

  const [values, setValues] = createSignal<FormValues>(emptyForm());
  const [hydrated, setHydrated] = createSignal(false);

  const skillsQuery = createQuery(() => ({
    queryKey: ["skills"],
    queryFn: fetchSkills,
  }));
  const projectsQuery = createQuery(() => ({
    queryKey: ["my-projects"],
    queryFn: fetchMyProjects,
    enabled: !isNew(),
  }));

  createEffect(() => {
    if (isNew() || hydrated()) return;
    const project = projectsQuery.data?.data.find((p) => p.id === projectId());
    if (!project) return;
    setValues({
      title: project.title ?? "",
      summary: project.summary ?? "",
      description: project.description ?? "",
      repoUrl: project.repo_url ?? "",
      demoUrl: project.demo_url ?? "",
      articleUrl: project.article_url ?? "",
      isPinned: !!project.is_pinned,
      visibility: project.visibility ?? "directory",
      skillIds: [...(project.skill_tag_ids ?? project.skills?.map((s) => s.id) ?? [])],
    });
    setHydrated(true);
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["my-projects"] });
    queryClient.invalidateQueries({ queryKey: ["member-profile"] });
  }

  const saveMutation = createMutation(() => ({
    mutationFn: () => {
      const v = values();
      const payload = {
        title: v.title.trim(),
        summary: v.summary.trim() || undefined,
        description: v.description.trim() || undefined,
        repo_url: v.repoUrl.trim() || undefined,
        demo_url: v.demoUrl.trim() || undefined,
        article_url: v.articleUrl.trim() || undefined,
        is_pinned: v.isPinned,
        visibility: v.visibility,
        skill_tag_ids: v.skillIds,
      };
      return isNew() ? createProject(payload) : updateProject(projectId(), payload);
    },
    onSuccess: () => {
      invalidate();
      navigate({ to: "/me" });
    },
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: () => deleteProject(projectId()),
    onSuccess: () => {
      invalidate();
      navigate({ to: "/me" });
    },
  }));

  function toggleSkill(id: string) {
    setValues((prev) => ({
      ...prev,
      skillIds: prev.skillIds.includes(id)
        ? prev.skillIds.filter((x) => x !== id)
        : prev.skillIds.length < MAX_SKILLS
          ? [...prev.skillIds, id]
          : prev.skillIds,
    }));
  }

  function onDelete() {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    deleteMutation.mutate();
  }

  return (
    <section class="page">
      <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
        <p class="status-chip">Portfolio</p>
        <h1>{isNew() ? "Add a project" : "Edit project"}</h1>
        <p class="muted">Title and a short summary are enough. Links stay as you type them.</p>
      </div>

      <div class="card" style={{ margin: "16px 20px" }}>
        <Show when={!isNew() && projectsQuery.isPending}>
          <p class="muted">Loading project…</p>
        </Show>
        <form
          class="wizard-form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
        >
          <label class="field">
            <span>Title</span>
            <input
              required
              maxlength={MAX_TITLE}
              value={values().title}
              onInput={(e) => setValues((prev) => ({ ...prev, title: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Summary</span>
            <input
              maxlength={MAX_SUMMARY}
              value={values().summary}
              placeholder="One line for directory and profile cards"
              onInput={(e) => setValues((prev) => ({ ...prev, summary: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Description</span>
            <textarea
              rows={5}
              maxlength={MAX_DESCRIPTION}
              value={values().description}
              onInput={(e) => setValues((prev) => ({ ...prev, description: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Repo URL</span>
            <input
              type="url"
              value={values().repoUrl}
              onInput={(e) => setValues((prev) => ({ ...prev, repoUrl: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Demo URL</span>
            <input
              type="url"
              value={values().demoUrl}
              onInput={(e) => setValues((prev) => ({ ...prev, demoUrl: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Article URL</span>
            <input
              type="url"
              value={values().articleUrl}
              onInput={(e) => setValues((prev) => ({ ...prev, articleUrl: e.currentTarget.value }))}
            />
          </label>

          <label class="switch-row">
            <span>Pin this project on my profile</span>
            <input
              type="checkbox"
              checked={values().isPinned}
              aria-label="Pin this project on my profile"
              onChange={(e) => setValues((prev) => ({ ...prev, isPinned: e.currentTarget.checked }))}
            />
          </label>
          <label class="switch-row">
            <span>Visible in the directory</span>
            <input
              type="checkbox"
              checked={values().visibility === "directory"}
              aria-label="Visible in the directory"
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  visibility: e.currentTarget.checked ? "directory" : "hidden",
                }))
              }
            />
          </label>

          <Show when={skillsQuery.data} keyed>
            {(payload) => (
              <div>
                <p class="muted">Skills (up to {MAX_SKILLS})</p>
                <For each={skillsByCategory(payload.data)}>
                  {([category, tags]) => (
                    <div class="skill-category">
                      <p class="skill-category-label">{SKILL_CATEGORY_LABELS[category] ?? category}</p>
                      <div class="interest-grid">
                        <For each={tags}>
                          {(tag) => (
                            <button
                              type="button"
                              class={`skill-chip skill-chip--toggle${values().skillIds.includes(tag.id) ? " selected" : ""}`}
                              aria-pressed={values().skillIds.includes(tag.id)}
                              onClick={() => toggleSkill(tag.id)}
                            >
                              {tag.label}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            )}
          </Show>

          <Show when={saveMutation.isError}>
            <div class="error-box">{(saveMutation.error as Error).message}</div>
          </Show>
          <Show when={deleteMutation.isError}>
            <div class="error-box">{(deleteMutation.error as Error).message}</div>
          </Show>

          <div class="wizard-actions">
            <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/me" })}>
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={saveMutation.isPending || !values().title.trim()}
            >
              {saveMutation.isPending ? "Saving…" : isNew() ? "Add project" : "Save project"}
            </button>
          </div>
        </form>

        <Show when={!isNew()}>
          <button
            type="button"
            class="btn btn-ghost"
            style={{ "margin-top": "12px", width: "100%" }}
            disabled={deleteMutation.isPending}
            onClick={onDelete}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete project"}
          </button>
        </Show>
      </div>
    </section>
  );
}
